<template>
  <div :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]">
    <div :class="$style.left">
      <BaseText
        v-show="changedServices.length"
        color="error"
        @click="onResetClick"
        v-test="'btn-price-change-reset'"
      >
        {{ $t('global.actions.reset') }}
      </BaseText>
    </div>
    <div :class="$style.right">
      <BaseButton
        v-if="!scheduledPriceUpdate || isEditing"
        color="inverted"
        :disabled="!changedServices.length"
        :loading="loading"
        @click="onScheduleClick"
        v-test="'btn-price-change-schedule'"
      >
        {{ $t('products.schedule_prices') }}
      </BaseButton>
      <BaseButton
        :disabled="!changedServices.length"
        :loading="loading"
        :notification="changedServices.length"
        @click="onUpdateNowClick"
        v-test="'btn-price-change-update-now'"
      >
        {{ $t('products.update_now') }}
      </BaseButton>
    </div>
    <ScheduleModal
      v-if="showScheduleModal"
      @close="showScheduleModal = false"
      @submit="submitSchedule"
    />
  </div>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import filters from '@/filters';
import { flash, modal } from '@/helpers/ui';
import { useServiceGroupsStore } from '@/stores/service-groups';
import { useServicesStore } from '@/stores/services';
import { useServicesPriceScheduleStore } from '@/stores/services-price-schedule';
import {
  CREATE_SERVICE_PRICE_CHANGE_SCHEDULE,
  UPDATE_SERVICE_PRICE_CHANGE_SCHEDULE,
} from './graphql';
import ScheduleModal from './ScheduleModal.vue';
import type { Change, TableData } from './types';

const { t } = useI18n();

const props = defineProps<{
  isEditing?: boolean;
  modelValue: TableData[];
}>();
const emit = defineEmits(['update:modelValue', 'submitted']);

const { scheduledPriceUpdate } = storeToRefs(useServicesPriceScheduleStore());

const showScheduleModal = ref(false);
const changedServices = computed(() =>
  props.modelValue.filter((change) => change.newPrice !== change.originalPrice),
);
const editedServices = computed(() => {
  return props.modelValue.filter(
    (change) => change.id || change.newPrice !== change.originalPrice,
  );
});

const onScheduleClick = () => {
  showScheduleModal.value = true;
};

const onUpdateNowClick = () => {
  modal('confirmation', {
    message: t(
      'services.price_update.apply_now_confirmation',
      { amount: changedServices.value.length },
      changedServices.value.length,
    ),
  }).then(() =>
    submitPriceUpdate().then(() => {
      const { getServices } = useServicesStore();
      const { getServiceGroups } = useServiceGroupsStore();

      Promise.all([getServices(), getServiceGroups()]).then(() => {
        flash(t('global.flash.service_price_update_updated'));
      });
    }),
  );
};

const { mutate: createMutate, loading: createLoading } = useMutation(
  CREATE_SERVICE_PRICE_CHANGE_SCHEDULE,
);
const { mutate: updateMutate, loading: updateLoading } = useMutation(
  UPDATE_SERVICE_PRICE_CHANGE_SCHEDULE,
);
const loading = computed(() => createLoading.value || updateLoading.value);

const submitPriceUpdate = (scheduledAt: string | null = null) => {
  const servicePriceChangesAttributes: Change[] = editedServices.value.map(
    (change) => ({
      id: change.id || undefined,
      serviceId: change.serviceId,
      originalPrice: change.originalPrice,
      newPrice: change.newPrice,
      destroy: !!change.id && change.newPrice === change.originalPrice,
    }),
  );

  return new Promise<void>((resolve) => {
    (props.isEditing ? updateMutate : createMutate)({
      input: {
        id: scheduledPriceUpdate.value?.id || undefined,
        scheduledAt,
        servicePriceChangesAttributes,
      },
    }).then(({ data }) => {
      emit('submitted');
      if (scheduledAt) {
        scheduledPriceUpdate.value =
          data?.[
            props.isEditing
              ? 'updateServicePriceChangeSchedule'
              : 'createServicePriceChangeSchedule'
          ]?.servicePriceChangeSchedule;
      }
      resolve();
    });
  });
};

const resetServices = () => {
  const clearedValue = props.modelValue.map((service) => ({
    ...service,
    newPrice: service.originalPrice,
  }));

  emit('update:modelValue', clearedValue);
};

const onResetClick = () => {
  modal('confirmation', {
    message: t('services.price_update.reset_changes_confirmation'),
  }).then(() => resetServices());
};

const submitSchedule = (dateTime: string) => {
  modal('confirmation', {
    message: t(
      'services.price_update.schedule_confirmation',
      {
        amount: changedServices.value.length,
        date: filters.dateTime(dateTime),
      },
      changedServices.value.length,
    ),
  }).then(() => {
    showScheduleModal.value = false;
    submitPriceUpdate(dateTime).then(() => {
      flash(t('global.flash.service_price_update_scheduled'));
    });
  });
};
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: $spacing;
  width: 100%;

  &.smallScreen {
    flex-direction: column;
    align-items: stretch;
  }
}

.left {
  .base.smallScreen & {
    display: flex;
    justify-content: center;
  }
}

.right {
  display: flex;
  align-items: center;
  gap: $spacing;

  .base.smallScreen & {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
